import {isHandledCustomerReferenceError} from "@/lib/customer-reference-error";
import {useState, useEffect} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {Textarea} from "@sakura-grp/ui/textarea";
import {toast} from "@sakura-grp/ui/sonner";
import {updateHostessMemo} from "@/lib/api/customers";
import {useDirtyDialogGuard} from "@/hooks/use-dirty-dialog-guard";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";

interface HostessMemoEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  hostessId: string;
  currentMemo: string;
}

export function HostessMemoEditSheet({
  open,
  onOpenChange,
  customerId,
  hostessId,
  currentMemo,
}: HostessMemoEditSheetProps) {
  const [memo, setMemo] = useState(currentMemo);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setMemo(currentMemo);
    }
  }, [open, currentMemo]);

  const mutation = useMutation({
    mutationFn: () => updateHostessMemo(customerId, hostessId, memo),
    onSuccess: () => {
      toast.success("メモを更新しました");
      queryClient.invalidateQueries({queryKey: ["customer-detail", customerId]});
      onOpenChange(false);
    },
    onError: (error: Error) => {
      if (isHandledCustomerReferenceError(error)) return;
      toast.error(error.message);
    },
  });

  const hasChanges = memo !== currentMemo;

  const {DirtyConfirmDialog, requestClose} = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });

  // vaul の repositionInputs は iOS で visualViewport.offsetTop 非考慮の
  // 二重シフトを起こすため無効化し、自前の inset でフッターをキーボード直上に
  // 密着させる (vaul#620 / docs/design/20260611-bizcard-drawer-keyboard-inset.md)
  const keyboardInset = useKeyboardInset();

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        dismissible={!hasChanges}
        repositionInputs={false}
      >
        <DrawerContent
          className="px-lg pb-safe-min"
          style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}
        >
          <DrawerHeader className="pb-md">
            <DrawerTitle>ホステスメモの編集</DrawerTitle>
            <DrawerDescription>この顧客へのメモを編集します</DrawerDescription>
          </DrawerHeader>

          <Textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="メモを入力..."
            rows={6}
            className="min-h-0 resize-none text-base"
          />

          <DrawerFooter className="p-0 pt-md flex shrink-0 flex-col gap-md">
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !hasChanges}
            >
              {mutation.isPending ? "保存中..." : "保存する"}
            </Button>
            <Button
              variant="outline"
              onClick={requestClose}
              disabled={mutation.isPending}
            >
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <DirtyConfirmDialog />
    </>
  );
}
